import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { organizationApi } from "../../../../../../../../services/Api/Organization/organizationApi";
import { SubscriptionsApi } from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { LoanApi } from "../../../../../../../../services/Api/types";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import CancelLoanModal from "../../../../../../components/Loan/CancelLoanModal";
import FormatLoanStatus from "../../../../../Partner/pages/Preview/components/FormatLoanStatus";
import AcceptLoan from "../../components/AcceptLoan";

interface MatchParams {
	loanId: string;
}

const OrganizationLoanPreviewPage = () => {
	const { loanId } = useParams<MatchParams>();
	const [loan, setLoan] = useState<undefined | LoanApi>(undefined);
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const { addFlash } = useFlash();
	const history = useHistory();
	const [estimatedLoan, setEstimatedLoan] = useState<SubscriptionsApi | undefined>(undefined);
	const [showCancelLoanModal, setShowCancelLoanModal] = useState<boolean>(false);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		onFetchLoan();
	}, []);

	const onFetchLoan = () => {
		const params = { include: "organization,partner,items,attachments" };
		Promise.all([organizationApi.getLoan(Number(loanId), params), organizationApi.getEstimatedLoan(Number(loanId))])
			.then((res) => {
				setLoan(res[0]);
				setEstimatedLoan(res[1]);
				handleChangeTabTitle(`#${res[0].reference_id} | ${t("common.word.loans")}`);
			})
			.catch((err) => {
				handleError.alert(err, addFlash);
			});
	};

	const onSuccessCloseLoan = () => {
		onFetchLoan();
	};

	if (!loan || !estimatedLoan) return <LoadingContainer />;

	const renderTitle = () => (
		<>
			{t("common.word.loans")} #{loan.reference_id} <FormatLoanStatus status={loan.status} />
		</>
	);

	const buttons: ButtonProps[] = [
		{
			title: t("modules.loan.action.cancel_loan.title"),
			variant: "primary",
			hide: loan.status !== "CLOSED",
			action: () => setShowCancelLoanModal(true),
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("modules.loan.action.cancel_loan.title"),
			hide: loan.status !== "CLOSED",
			action: () => setShowCancelLoanModal(true),
		},
	];

	return (
		<>
			<MobileActions actions={mobileActions} />
			<Header
				title={renderTitle()}
				back={() => history.push(`/${organization.id}/loans`)}
				buttons={isMobile ? [] : buttons}
			/>
			<div className={"content-preview"}>
				<AcceptLoan handleUpdate={setLoan} estimatedLoan={estimatedLoan} loan={loan} />
			</div>
			{showCancelLoanModal && (
				<CancelLoanModal
					onHide={() => setShowCancelLoanModal(false)}
					isShown={showCancelLoanModal}
					isAdminMode={false}
					onSuccessfulCancelAction={onSuccessCloseLoan}
					onSuccessfulGenerateAction={onSuccessCloseLoan}
					loan={loan}
					partnerId={undefined}
				/>
			)}
		</>
	);
};
export default OrganizationLoanPreviewPage;
